import BaseFormItem from "../BaseFormItem";
import { descriptorPropTypes } from "lib/common/descriptorcontrols/formhelpers";
import EntityProxy from "data/util/EntityProxy";
import PropTypes from "prop-types";
import {
    clientFilteredSearch,
    serverFilteredSearch,
} from "data/SuperRecordActions";
import {
    getCandidateFilter,
    getPartialCandidateFilter,
} from "data/selectors/entityFields";
import entityFieldMetaStore from "data/meta/entityFieldMetaStore";
import React from "react";
import Control from "lib/common/descriptorcontrols/AssociationField/typeahead";
import pathWalker from "jsmodel/pathWalker";
import SearchResultStore from "data/SearchResultStore";
import SuperRecordListStore from "data/SuperRecordListStore";
import { getSuperRecordListRecords } from "data/SuperRecordListActions";
import SuperRecordStore from "data/SuperRecordStore";
import useFluxStore from "@armus/use-flux-store";

const buildConfig = descriptor => {
    const baseConfig = {
        searchPageSize: 20,
        searchDebounce: 250,
        valueKey: "id",
        labelKey: "_displayString",
        isServer: false,
        mode: "client",
        multiValued: false,
    };
    const config = {...baseConfig, ...descriptor.config} || baseConfig;
    config.columns = config.columns || [{key: config.valueKey, label: config.labelKey}];
    config.isServer = (config.mode === "server") && (config.otherEntityKey != null);
    return config;
};

const getConnectedCtrl = (config) => {
    // choose which DataGateway to use.
    const withData = config.isServer ?
        withServerDataGateway : config.otherEntityType === "RECORD" ?
            withSourceCollectionDataGateway : OtherEntityRecordsDataGateway;
    return withData(props => {
        const {value, allRecords, builder, enabled} = props;
        // convert allRecords into options prop
        return <Control {...props}
                        options={builder(value, allRecords, enabled)}
        />;
    });
};

export class AssociationFieldtypeahead extends BaseFormItem {

    constructor(...args) {
        super(...args);
        // descriptor is static, so this is safe.
        this.config = buildConfig(this.props.descriptor);
        this.buildOptions = this.buildOptions.bind(this);
        this.onRecordbrowserClick = this.onRecordbrowserClick.bind(this);
        this.serverSearch = this.serverSearch.bind(this);

        //
        this.control = getConnectedCtrl(this.config);
    }

    getCandidateFilter() {
        const {activeRecord, descriptor} = this.props;
        return getCandidateFilter(
            entityFieldMetaStore.getInfo(
                activeRecord.entityKey,
                descriptor.key,
            ),
            activeRecord,
        );
    }

    getPartialCandidateFilter() {
        const {activeRecord, descriptor} = this.props;
        return getPartialCandidateFilter(
            entityFieldMetaStore.getInfo(
                activeRecord.entityKey,
                descriptor.key,
            ),
            activeRecord,
        );
    }

    onRecordbrowserClick(e) {
        const { onGlobalRecordLookup, descriptor} = this.props;
        e.stopPropagation();
        onGlobalRecordLookup(
            descriptor.config.otherEntityKey,
            descriptor.key,
            this.getCandidateFilter()
        );
    }

    onCommitChange(d, v, userInvoked = true, label = null) {
        const {config} = this;
        if(config.multiValued) {
            if(Array.isArray(v)) {
                v = v.map((it) => it[config.valueKey]);
            }
            else {
                v = null;
            }
        }
        else {
            v = (v === null) ? null : v[config.valueKey];
        }
        super.onCommitChange(d, v, userInvoked, label);
    }

    buildOptions(currValue, allRecords, enabled) {
        const {descriptor} = this.props;
        const {config} = this;
        const candidateFilter = this.getCandidateFilter();
        const filteredOptions = [];
        const keys = _.pluck(config.columns, "key").filter(it => it !== "id");
        const vals = currValue ? (Array.isArray(currValue) ? currValue : [currValue]) : [];

        const buildOptionFromRecord = (record) => {
            const o = {
                record // this provides quicker lookup on the onCommitChange side.
            };
            _.each(keys, key => o[key] = (record.get(key) || "").toString());
            o[config.valueKey] = record.id;
            o[config.labelKey] = record.getLabel();
            return o;
        };

        allRecords.forEach((record) => {
            const o = buildOptionFromRecord(record);
            // include selected value in the list even if it was filtered out.
            const hasValue = vals.map(i => i.record.id).includes(record.id);
            if(hasValue || (record.isRecordEnabled() && candidateFilter(record))) {
                filteredOptions.push(o);
            }
        });

        if(config.isServer && !config.multiValued && currValue) {
            // for server requests we need to add back the selected value if
            // it does not exist in the returned list.
            vals.forEach((v) => {
                if(filteredOptions.find((o) => o[config.valueKey] === v[config.valueKey])) {
                    // selected value already in list
                    return;
                }
                const o = buildOptionFromRecord(v.record);
                filteredOptions.unshift(o);
            });
        }
        // autoSelectOnlyOption only option. Only when the field is enabled.
        if(enabled && config.autoSelectOnlyOption && filteredOptions.length === 1 && currValue === null) {

            // Because this may be invoked during component construction, it
            // can't do a synchonous dispatch without violating React's rules
            // about state changes. At least for the moment. Once we don't have
            // all the synchronous backbone-based flows, it can probably vanish.
            //
            // eslint-disable-next-line no-settimeout
            setTimeout(() =>
                    this.onCommitChange(descriptor, filteredOptions[0]));
        }

        // also make sure the selected value labels are up to date.
        return filteredOptions;

    };

    // builds query for clientFilteredSearch, invokes it, and returns the LO
    serverSearch(query, candidateFilter, partialCandidateFilter, sort = {}) {
        const { config } = this;
        const otherEntityKey = config.otherEntityKey;
        const columns = config.columns;
        let filters = [];
        if (query) {
            columns.forEach(c => {
                if ((c.includeInFilter == null) || c.includeInFilter) {
                    return filters.push(c.key + ".toString().toLowerCase().contains(\"" + query.toLowerCase() + "\")");
                }
            });
        }
        let orderer = "";
        if (sort.key) {
            orderer = sort.key + " " + sort.dir;
        }
        if(partialCandidateFilter != null) {
            // server side filtering
            if (filters.length > 0) {
                filters = "(" + filters.join("||") + ")" + (partialCandidateFilter ? `&&(${partialCandidateFilter})` : "");
            } else {
                filters = partialCandidateFilter || "true";
            }
            return serverFilteredSearch(
                SearchResultStore,
                otherEntityKey,
                filters,
                orderer,
                config.searchPageSize + 1, // size
                1, // page
                false, // showLoadingModal
                true, // onlyEnabledRecords
            );
        }
        else {
            // client side filtering
            if (filters.length > 0) {
                filters = filters.join("||");
            } else {
                filters = "true";
            }
            return clientFilteredSearch(
                SearchResultStore,
                otherEntityKey,
                filters,
                orderer,
                config.searchPageSize + 1,
                r => r.isRecordEnabled() && candidateFilter(r),
                false, // showLoadingModal
            );
        }
    }

    buildValue(v) {
        const {config} = this;
        const buildValue = (r) => ({
            [config.valueKey]: r.id,
            [config.labelKey]: r.getLabel(),
            record: r,
        });

        let value = null;
        if(v != null){
            if(config.multiValued) {
                value = v.map((r) => buildValue(r));
            }
            else {
                value = buildValue(v);
            }
        }
        return value;
    }

    render() {
        const {activeRecord, descriptor, fluxContext} = this.props;
        const {config} = this;
        const ConnectedCtrl = this.control;
        return <ConnectedCtrl {...this.getPlumbingProps()}
                         enabled={activeRecord.isFieldEnabled(descriptor.key)}
                         valueKey={config.valueKey}
                         labelKey={config.labelKey}
                         value={this.buildValue(activeRecord.get(descriptor.key))}
                         isLoading={true}
                         isPartial={false}
                         isError={false}
                         onRecordbrowserClick={this.onRecordbrowserClick}

                         // for the data connection
                         isServer={config.isServer}
                         otherEntityKey={config.otherEntityKey}
                         activeRecord={activeRecord}
                         fluxContext={fluxContext}
                         builder={this.buildOptions}
                         serverSearch={this.serverSearch}
                         candidateFilter={this.getCandidateFilter()}
                         partialCandidateFilter={this.getPartialCandidateFilter()}
                         searchPageSize={config.searchPageSize}
        />;
    }
}

const withServerDataGateway = WrappedComponent => {
    const WithSearchResults = props => {
        const {
            serverSearch,
            candidateFilter,
            partialCandidateFilter,
            text,
            sort,
            searchPageSize,
        } = props;
        const state = useFluxStore(() => {
            const lo = serverSearch(text, candidateFilter, partialCandidateFilter, sort);
            let allRecords = [];
            if (lo.hasValue()) {
                const eps = lo.getRequiredValue();
                const mv = Array.isArray(eps);
                allRecords = (mv ? eps : [eps]);
            }

            return {
                isLoading: lo.isLoading(),
                isError: lo.hasError(),
                allRecords: allRecords,
                isPartial: (allRecords.length > searchPageSize),
            };
        }, [
            SuperRecordStore,
            SearchResultStore,
        ], [serverSearch, candidateFilter, text, sort, searchPageSize]);
        const newProps = {...props};
        delete newProps.activeRecord;
        return <WrappedComponent {...newProps} {...state} />;
    };

    WithSearchResults.propTypes = {
        serverSearch: PropTypes.func.isRequired,
        candidateFilter: PropTypes.func.isRequired,
        partialCandidateFilter: PropTypes.string,
        text: PropTypes.string.isRequired,
        sort: PropTypes.object,
        searchPageSize: PropTypes.number.isRequired,
    };

    class ServerDataGateway extends React.PureComponent {
        constructor(props) {
            super(...arguments);
            this.state = {
                text: "",
                sort: {},
            };
            this.onSearch = this.onSearch.bind(this);
        }

        onSearch(d, text, sort) {
            this.setState({text, sort});
        }

        render() {
            const newProps = {...this.props};
            delete newProps.activeRecord;
            return (
                <WithSearchResults
                    onSearch={this.onSearch}
                    {...newProps}
                    {...this.state}
                />
            );
        }
    }
    ServerDataGateway.propTypes = {
        isServer: PropTypes.bool.isRequired,
        serverSearch: PropTypes.func.isRequired,
        candidateFilter: PropTypes.func.isRequired,
        onFocus: PropTypes.func.isRequired
    };

    return ServerDataGateway;
};

const withSourceCollectionDataGateway = (WrappedComponent) => {
    const SourceCollectionDataGateway = (props) => {
        const {activeRecord, descriptor} = props;
        const config = descriptor.config || {};
        const newProps = {...props};
        delete newProps.activeRecord;
        return <WrappedComponent
            {...newProps}
            isLoading={false}
            allRecords={pathWalker(activeRecord, config.sourceCollectionPath) || []}
        />;
    };

    SourceCollectionDataGateway.propTypes = {
        activeRecord: PropTypes.instanceOf(EntityProxy).isRequired,
        descriptor: PropTypes.shape({
            config: PropTypes.shape({
                sourceCollectionPath: PropTypes.string.isRequired,
            }).isRequired,
        }).isRequired,
    };
    return SourceCollectionDataGateway;
};

const OtherEntityRecordsDataGateway = (WrappedComponent) => props => {
    // eslint-disable-next-line react/prop-types
    const { fluxContext, otherEntityKey } = props;
    const state = useFluxStore(() => {
        const lo = getSuperRecordListRecords(otherEntityKey, fluxContext);
        return {
            isLoading: lo.isLoading(),
            isError: lo.hasError(),
            allRecords: lo.hasValue() ? lo.getValue() || [] : [],
        };
    }, [
        SuperRecordListStore,
    ], [fluxContext, otherEntityKey]);
    const newProps = {...props};
    delete newProps.activeRecord;
    return <WrappedComponent {...newProps} {...state} />;
};

AssociationFieldtypeahead.propTypes = {
    ...AssociationFieldtypeahead.propTypes,
    descriptor: PropTypes.shape(descriptorPropTypes({
        mode: PropTypes.string,
        multiValued: PropTypes.bool.isRequired,
        otherEntityKey: PropTypes.string.isRequired,
        otherEntityType: PropTypes.string.isRequired
    })),
};

export default AssociationFieldtypeahead;



// WEBPACK FOOTER //
// ./app/lib/dataentry/lib/dataentryform/formitems/AssociationField/typeahead.js